<template>
  <v-card outlined class="rounded-lg">
    <v-card-text>
      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-bug-outline</v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="subtitle-2 font-weight-bold">Bug Tickets</div>
          <div class="caption grey--text">
            {{ tickets.length === 0 ? 'No tickets yet' : `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` }}
          </div>
        </div>
        <v-btn small color="primary" depressed class="text-none rounded-lg" @click="dialog = true">
          <v-icon small left>mdi-plus</v-icon>Report a bug
        </v-btn>
      </div>

      <!-- Empty state -->
      <div v-if="tickets.length === 0" class="text-center py-6">
        <div class="body-2 grey--text font-italic" style="line-height:1.7;">
          Use "Report a bug" at the top<br>to create a ticket.
        </div>
      </div>

      <!-- Ticket list (slim rows) -->
      <div
        v-for="t in visibleTickets"
        :key="t.id"
        class="d-flex align-center px-3 py-2 rounded-lg mb-1"
        style="background:#f5f5f5;border:1px solid #e0e0e0;gap:8px;cursor:pointer;"
        @click="selectedTicket = t; detailDialog = true"
      >
        <span class="caption font-weight-bold grey--text" style="font-family:monospace;white-space:nowrap;font-size:11px;">{{ t.number }}</span>
        <v-chip x-small :color="t.status === 'resolved' ? 'success' : t.status === 'in_progress' ? 'warning' : 'default'" style="flex-shrink:0;">
          {{ t.status || 'open' }}
        </v-chip>
        <span class="flex-grow-1" />
        <span class="caption grey--text" style="white-space:nowrap;font-size:11px;">{{ formatShortDate(t.date) }}</span>
        <v-icon size="14" class="grey--text">mdi-chevron-right</v-icon>
      </div>

      <!-- View more / view all link -->
      <div v-if="tickets.length > 0" class="text-center mt-2">
        <v-btn
          text
          small
          color="primary"
          class="text-none rounded-lg"
          @click="viewAll"
        >
          View more
          <v-icon small right>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </v-card-text>

    <!-- Ticket Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="520" scrollable>
      <v-card v-if="selectedTicket" class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between pb-1">
          <div>
            <span class="caption grey--text font-weight-bold" style="font-family:monospace;">{{ selectedTicket.number }}</span>
            <v-chip x-small class="ml-2" :color="selectedTicket.status === 'resolved' ? 'success' : selectedTicket.status === 'in_progress' ? 'warning' : 'default'">{{ selectedTicket.status || 'open' }}</v-chip>
          </div>
          <v-btn icon small @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4" style="max-height:70vh;overflow-y:auto;">
          <div class="subtitle-1 font-weight-bold mb-3">{{ selectedTicket.title }}</div>

          <div class="caption grey--text text-uppercase font-weight-bold mb-1">Severity</div>
          <v-chip small :color="selectedTicket.severity === 'high' ? 'error' : selectedTicket.severity === 'medium' ? 'warning' : 'default'" class="mb-3">
            {{ selectedTicket.severity || 'medium' }}
          </v-chip>

          <div class="caption grey--text text-uppercase font-weight-bold mb-1">Description</div>
          <div class="body-2 mb-4" style="white-space:pre-wrap;line-height:1.6;">{{ selectedTicket.description || '—' }}</div>

          <template v-if="selectedTicket.screenshots && selectedTicket.screenshots.length">
            <div class="caption grey--text text-uppercase font-weight-bold mb-2">Screenshots</div>
            <div class="d-flex flex-wrap" style="gap:8px;">
              <a v-for="(s, i) in selectedTicket.screenshots" :key="i" :href="typeof s === 'string' ? s : (s.url || s.path || '#')" target="_blank">
                <v-img :src="typeof s === 'string' ? s : (s.url || s.path || '')" width="100" height="80" class="rounded-lg" contain style="background:#f0f0f0;border:1px solid #e0e0e0;" />
              </a>
            </div>
          </template>

          <div class="caption grey--text mt-4">Submitted {{ formatDate(selectedTicket.date) }}</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Report Bug Dialog -->
    <v-dialog v-model="dialog" max-width="480">
      <v-card class="rounded-lg">
        <v-card-title class="subtitle-1 font-weight-bold">Report a Bug</v-card-title>
        <v-card-text>
          <div class="caption grey--text text-uppercase font-weight-medium mb-1">Title</div>
          <v-text-field v-model="draft.title" dense outlined hide-details class="mb-3 rounded-lg" />
          <div class="caption grey--text text-uppercase font-weight-medium mb-1">Description</div>
          <v-textarea v-model="draft.description" dense outlined hide-details rows="3" class="mb-3 rounded-lg" />
          <div class="caption grey--text text-uppercase font-weight-medium mb-1">Severity</div>
          <v-select v-model="draft.severity" :items="['low','medium','high']" dense outlined hide-details class="rounded-lg mb-3" />

          <div class="caption grey--text text-uppercase font-weight-medium mb-1">Screenshots <span class="text-lowercase grey--text">(optional)</span></div>
          <v-file-input
            v-model="draft.screenshots"
            multiple
            dense
            outlined
            hide-details
            show-size
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
            prepend-icon=""
            prepend-inner-icon="mdi-image-multiple-outline"
            placeholder="Attach one or more images"
            class="rounded-lg"
            :disabled="submitting"
          >
            <template v-slot:selection="{ text, index }">
              <v-chip small color="primary" outlined close @click:close="removeScreenshot(index)">
                <v-icon x-small left>mdi-image-outline</v-icon>
                {{ text }}
              </v-chip>
            </template>
          </v-file-input>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text class="text-none grey--text" :disabled="submitting" @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed class="text-none rounded-lg" :loading="submitting" @click="submit">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'BugTicketsCard',
  data() {
    return {
      loading: false,
      submitting: false,
      dialog: false,
      detailDialog: false,
      selectedTicket: null,
      draft: { title: '', description: '', severity: 'medium', screenshots: [] },
      tickets: [],
    };
  },
  computed: {
    // Show only the 3 most recent tickets on the card; full list lives on /employee-growth/bugs.
    visibleTickets() {
      return (this.tickets || []).slice(0, 5);
    },
  },
  mounted() {
    this.load();
    this.$root.$on('growth:open-bug-dialog', this.openDialog);
  },
  beforeDestroy() {
    this.$root.$off('growth:open-bug-dialog', this.openDialog);
  },
  methods: {
    viewAll() {
      this.$router.push('/employee-growth/bugs');
    },
    openDialog() {
      this.draft = { title: '', description: '', severity: 'medium', screenshots: [] };
      this.dialog = true;
    },
    removeScreenshot(index) {
      const next = [...(this.draft.screenshots || [])];
      next.splice(index, 1);
      this.draft.screenshots = next;
    },
    mapTicket(t) {
      return {
        id: t.id,
        number: t.ticket_number,
        title: t.title,
        description: t.description,
        severity: t.severity,
        status: t.status,
        screenshots: Array.isArray(t.screenshots) ? t.screenshots : [],
        date: t.created_at,
      };
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('api/employee-growth/bugs');
        if (res.data.response) {
          this.tickets = res.data.data.map(this.mapTicket);
        }
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.draft.title.trim()) { this.$toast && this.$toast.error('Add a title'); return; }
      this.submitting = true;
      try {
        // Use multipart/form-data (same pattern as /api/resources) so the
        // `screenshots[]` uploads arrive as real UploadedFile instances
        // on the backend. Axios plugin preserves the browser-set boundary.
        const fd = new FormData();
        fd.append('title', this.draft.title);
        fd.append('description', this.draft.description || '');
        fd.append('severity', this.draft.severity || 'medium');
        (this.draft.screenshots || []).forEach((f) => {
          if (f instanceof File) fd.append('screenshots[]', f);
        });

        const res = await this.$axios.post('api/employee-growth/bugs', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res.data.response) {
          this.tickets.unshift(this.mapTicket(res.data.data));
          this.draft = { title: '', description: '', severity: 'medium', screenshots: [] };
          this.dialog = false;
          this.$toast && this.$toast.success('Bug ticket submitted!');
        }
      } catch (e) {
        this.$toast && this.$toast.error('Failed to create ticket');
      } finally {
        this.submitting = false;
      }
    },
    formatShortDate(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    formatDate(iso) {
      // $date/$tz so this follows the display timezone and clock settings.
      const d = this.$date(iso);
      if (!d.isValid()) return iso;
      return d.format(`MMM D, YYYY ${this.$tz.time}`);
    },
  },
};
</script>
